import React from "react";
import manifest from "./ImageWithOptimizedVariants.manifest.json";

type ManifestVideo = {
  // Three-codec ladder for animated content. h264 is required: it's the
  // universal fallback that every browser in our floor (Chrome 73+, Firefox
  // 78+, Edge 99+, Safari 14+) can play. av1 (Chrome 70+, Firefox 113+,
  // Safari 17+) and vp9 (Chrome 25+, Firefox 28+, Edge 79+, Safari 14.1+)
  // are progressive enhancements; older browsers fall through to h264.
  av1?: string;
  vp9?: string;
  h264: string;
  poster?: string;
};

type ManifestEntry = {
  // avif/webp may be absent (e.g. animated GIFs where lossy GIF beats animated WebP).
  // When both are absent the component renders a plain <img src={fallback}> instead of <picture>.
  avif?: string;
  webp?: string;
  fallback: string;
  avif2x?: string;
  webp2x?: string;
  fallback2x?: string;
  // When present, the component renders a muted/looping/autoplay <video>
  // instead of an <img>. Used for animated GIFs that we've re-encoded into
  // AV1/VP9/H.264. The optimized GIF stays in `fallback` as deeper insurance
  // for any browser that can't play <video> at all (essentially zero in 2026).
  video?: ManifestVideo;
  width?: number;
  height?: number;
};

const MANIFEST: Record<string, ManifestEntry> = manifest as Record<string, ManifestEntry>;

// Build a secondary lookup keyed by URL pathname so that path-only `src`
// values (e.g. "/images/tutoring/hero-image.png" or fingerprinted asset paths
// returned by ES module imports) resolve to manifest entries whose keys
// include an origin (e.g. "http://localhost:8000/images/tutoring/hero-image.png").
const PATH_INDEX: Record<string, ManifestEntry> = (() => {
  const idx: Record<string, ManifestEntry> = {};
  for (const [key, entry] of Object.entries(MANIFEST)) {
    try {
      const u = new URL(key);
      idx[u.pathname] = entry;
    } catch {
      // Non-URL keys: index as-is.
      idx[key] = entry;
    }
  }
  return idx;
})();

/**
 * Resolve a remote image URL to its locally optimized fallback path, if
 * available in the manifest. Use this for CSS background-image / inline-style
 * URLs where wrapping in `<picture>` is not possible. Returns the original URL
 * unchanged when there is no manifest entry.
 */
export function resolveOptimizedImageUrl(src: string): string {
  const entry = lookupManifest(src);
  return entry?.fallback ?? src;
}

function mimeTypeForFallback(fallback: string): string {
  const lower = fallback.toLowerCase();
  // Strip query/hash before checking the extension.
  const path = lower.split("?")[0].split("#")[0];
  if (path.endsWith(".jpg") || path.endsWith(".jpeg")) return "image/jpeg";
  if (path.endsWith(".gif")) return "image/gif";
  if (path.endsWith(".webp")) return "image/webp";
  if (path.endsWith(".avif")) return "image/avif";
  // Default — most of our optimized fallbacks are PNG.
  return "image/png";
}

/**
 * Resolve a remote image URL to a CSS declaration block suitable for direct
 * interpolation into an emotion-styled template. Emits a plain `url(...)`
 * fallback declaration first (used by older browsers in our floor: Chrome
 * 73–87, Firefox 78–88, Safari 14–17.3) followed by an `image-set(...)` with
 * `type()` annotations that newer browsers understand and override with
 * (Chrome 88+, Firefox 89+, Safari 17.4+). Browsers that don't understand the
 * `type()` syntax discard the entire `image-set()` declaration as invalid and
 * fall through to the `url(...)` form — net result: every browser fetches one
 * image, AVIF when supported, optimized PNG/JPG otherwise.
 *
 * - undefined/empty src → "" (caller renders the styled-div with no background)
 * - manifest entry with avif+webp → two declarations (url fallback + image-set)
 * - fallback-only manifest entry (e.g. animated GIFs) → single url() declaration
 * - non-manifest src → single url() declaration with the original src (matches
 *   resolveOptimizedImageUrl pass-through behavior)
 */
export function resolveOptimizedImageBackground(src: string | undefined): string {
  if (!src) return "";
  const entry = lookupManifest(src);
  if (!entry) {
    return `background-image: url("${src}");`;
  }
  if (!entry.avif || !entry.webp) {
    return `background-image: url("${entry.fallback}");`;
  }
  const fallbackType = mimeTypeForFallback(entry.fallback);
  return (
    `background-image: url("${entry.fallback}");` +
    `background-image: image-set(` +
    `url("${entry.avif}") type("image/avif"),` +
    `url("${entry.webp}") type("image/webp"),` +
    `url("${entry.fallback}") type("${fallbackType}")` +
    `);`
  );
}

function lookupManifest(src: string): ManifestEntry | undefined {
  const direct = MANIFEST[src];
  if (direct) return direct;
  // Try as a path-only lookup (strip origin if present).
  try {
    const u = new URL(src, "http://_relative_");
    return PATH_INDEX[u.pathname];
  } catch {
    return PATH_INDEX[src];
  }
}

type CommonImgAttributes = {
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "auto" | "low";
  decoding?: "sync" | "async" | "auto";
  sizes?: string;
  style?: React.CSSProperties;
};

type ManifestModeProps = CommonImgAttributes & {
  src: string;
  width?: number;
  height?: number;
  // explicit-mode discriminator must be absent
  avif?: never;
  webp?: never;
  fallback?: never;
  avif2x?: never;
  webp2x?: never;
  fallback2x?: never;
};

type ExplicitModeProps = CommonImgAttributes & {
  avif: string;
  webp: string;
  fallback: string;
  avif2x?: string;
  webp2x?: string;
  fallback2x?: string;
  width: number;
  height: number;
  // manifest-mode discriminator must be absent
  src?: never;
};

export type ImageWithOptimizedVariantsProps = ManifestModeProps | ExplicitModeProps;

function isExplicit(props: ImageWithOptimizedVariantsProps): props is ExplicitModeProps {
  return typeof (props as ExplicitModeProps).fallback === "string";
}

function buildSrcSet(oneX: string, twoX?: string): string {
  if (twoX) {
    return `${oneX} 1x, ${twoX} 2x`;
  }
  return oneX;
}

const ImageWithOptimizedVariants: React.FC<ImageWithOptimizedVariantsProps> = (props) => {
  const { alt, className, loading = "lazy", fetchPriority, decoding = "async", sizes, style } = props;

  // React 18.3 + react-dom DOM allowlist still warns on `fetchPriority` (camelCase).
  // The HTML attribute name is lowercase; spread the lowercase form to satisfy the DOM.
  const fetchPriorityAttr = fetchPriority ? { fetchpriority: fetchPriority } : {};

  // Explicit mode: always render <picture>.
  if (isExplicit(props)) {
    const { avif, webp, fallback, avif2x, webp2x, fallback2x, width, height } = props;

    return (
      <picture>
        <source type="image/avif" srcSet={buildSrcSet(avif, avif2x)} sizes={sizes} />
        <source type="image/webp" srcSet={buildSrcSet(webp, webp2x)} sizes={sizes} />
        <img
          src={fallback}
          srcSet={fallback2x ? buildSrcSet(fallback, fallback2x) : undefined}
          sizes={sizes}
          alt={alt}
          width={width}
          height={height}
          className={className}
          loading={loading}
          decoding={decoding}
          {...fetchPriorityAttr}
          style={style}
        />
      </picture>
    );
  }

  // Manifest mode
  const { src, width: propWidth, height: propHeight } = props;
  const entry = lookupManifest(src);

  // Width/height come from explicit props only — never from the manifest.
  // The wrap must mirror the pre-wrap markup: a dimensionless <img> in master
  // stays dimensionless after wrapping. Manifest entries' width/height fields
  // are inert.
  const width = propWidth;
  const height = propHeight;

  // src not in manifest: emit a plain <img>.
  if (!entry) {
    return (
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
        decoding={decoding}
        {...fetchPriorityAttr}
        sizes={sizes}
        style={style}
      />
    );
  }

  // Animated content with video variants: emit an inline autoplay/loop/muted
  // <video> with three codec sources. `aria-label` carries the alt text since
  // <video> doesn't accept an alt attribute. `disablePictureInPicture` keeps
  // the floating PiP affordance off these decorative loops on Safari.
  if (entry.video) {
    const { video } = entry;
    return (
      <video
        autoPlay
        loop
        muted
        playsInline
        disablePictureInPicture
        poster={video.poster}
        width={width}
        height={height}
        className={className}
        aria-label={alt}
        style={style}
      >
        {video.av1 && <source src={video.av1} type='video/mp4; codecs="av01.0.05M.08"' />}
        {video.vp9 && <source src={video.vp9} type='video/webm; codecs="vp9"' />}
        <source src={video.h264} type="video/mp4" />
      </video>
    );
  }

  // Fallback-only entry (e.g. animated GIFs without video variants): emit a
  // plain <img src=fallback> instead of <picture>. <picture> with no <source>
  // is pointless and a malformed <source> with empty srcset is worse.
  if (!entry.avif && !entry.webp) {
    return (
      <img
        src={entry.fallback}
        srcSet={entry.fallback2x ? buildSrcSet(entry.fallback, entry.fallback2x) : undefined}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
        decoding={decoding}
        {...fetchPriorityAttr}
        style={style}
      />
    );
  }

  return (
    <picture>
      {entry.avif && <source type="image/avif" srcSet={buildSrcSet(entry.avif, entry.avif2x)} sizes={sizes} />}
      {entry.webp && <source type="image/webp" srcSet={buildSrcSet(entry.webp, entry.webp2x)} sizes={sizes} />}
      <img
        src={entry.fallback}
        srcSet={entry.fallback2x ? buildSrcSet(entry.fallback, entry.fallback2x) : undefined}
        sizes={sizes}
        alt={alt}
        width={width}
        height={height}
        className={className}
        loading={loading}
        decoding={decoding}
        {...fetchPriorityAttr}
        style={style}
      />
    </picture>
  );
};

export default ImageWithOptimizedVariants;
